
export default [
  {
    "title": "Audit log",
    "summary": "What audit logs are for, where to find them, and how to read entries in the Security pages.",
    "order": 2,
    "published": true,
    "date": new Date("2026-06-04T00:00:00.000Z"),
    "content": "Audit logs help you answer “who did what, and when?” for your workspace and your own account. They are not a full web server access log — they record defined security and product actions plus successful public API calls.\n\n## Two logs\n\n| | Team audit log | Personal audit log |\n| --- | --- | --- |\n| **Scope** | Activity on the current team | Your user account only |\n| **Location** | **Team → Security** | **Account → Security** (profile menu) |\n| **Viewers** | **Owner** and **Admin** only | You, when signed in |\n| **Examples** | Invites, API keys, notifications, billing | Sign-in, MFA, password, email |\n\nMembers cannot open the team audit log. See [Team roles and permissions](/docs/knowledge-base/team-roles-and-permissions).\n\nFor a short description of every action label you might see, use the [audit log events](/docs/knowledge-base/audit-log-events) knowledge base article.\n\n## Open the team audit log\n\n1. Select the team in the sidebar switcher.\n2. Go to **Team → Security**.\n3. Review the summary cards, then scroll to **Audit Log History**.\n\nUse the table filter to search by action name. Pagination shows five rows per page by default.\n\n![Team Security page showing summary cards and the audit log table](/docs/knowledge-base/team-security-audit-overview.svg)\n\nSummary cards count total rows, team-creation-related rows, member-invite-related rows, and distinct IP addresses. Treat them as quick context, not a full analytics dashboard.\n\n## Open your personal audit log\n\n1. Open your profile menu and choose **Security** (or go to `/user/security`).\n2. Manage MFA at the top; your sign-in and account events appear in the table below.\n\n![Personal Security page with MFA and your sign-in history](/docs/knowledge-base/user-security-audit.svg)\n\n## Read a row\n\n| Column | What it tells you |\n| --- | --- |\n| **Action** | The event type. The UI shows spaces instead of underscores (for example `TEAM MEMBER INVITED`). |\n| **IP Address** | Client IP when the event was recorded. |\n| **User Agent** | Browser or client summary. Click the cell for full browser, OS, and device details. |\n| **Metadata** | Extra fields such as `actioned_by`, resource IDs, or API route details. |\n| **Created At** | Timestamp in your local timezone. |\n\nActions that include `FAILED` in the name often appear with error-style badge coloring.\n\n### Metadata basics\n\nMost team events include `actioned_by` (email of the signed-in user) and `user_id`. Notification and API key events include resource IDs such as `channel_id` or `api_key_id`. `API REQUEST` rows include `method`, `path`, `status`, and sometimes `api_key_id`.\n\nOnly **successful** `/api/v1` responses create `API REQUEST` rows. Failed API calls are not listed here.\n\n## Retention\n\nTeam audit history length depends on your subscription (`audit_retention_days`). When retention is reached, older team rows are removed automatically.\n\nThere is no in-app export today. Contact support if you need bulk export for compliance.\n\n## Limits\n\n- Refreshes are manual — the table does not live-update.\n- Not every button click is logged — only the event types in the [events reference](/docs/knowledge-base/audit-log-events).\n\n## Related articles\n\n- [Audit log events](/docs/knowledge-base/audit-log-events) — what each action name means  \n- [Team roles and permissions](/docs/knowledge-base/team-roles-and-permissions)  \n- [API key scopes](/docs/knowledge-base/api-key-scopes)  \n- [Creating API keys](/docs/creating-api-keys)",
    "_meta": {
      "filePath": "audit-log.mdx",
      "fileName": "audit-log.mdx",
      "directory": ".",
      "extension": "mdx",
      "path": "audit-log"
    },
    "mdxModule": "helpCenterPosts/audit-log",
    "tableOfContents": [
      {
        "title": "Two logs",
        "slug": "two-logs",
        "items": []
      },
      {
        "title": "Open the team audit log",
        "slug": "open-the-team-audit-log",
        "items": []
      },
      {
        "title": "Open your personal audit log",
        "slug": "open-your-personal-audit-log",
        "items": []
      },
      {
        "title": "Read a row",
        "slug": "read-a-row",
        "items": [
          {
            "title": "Metadata basics",
            "slug": "metadata-basics",
            "items": []
          }
        ]
      },
      {
        "title": "Retention",
        "slug": "retention",
        "items": []
      },
      {
        "title": "Limits",
        "slug": "limits",
        "items": []
      },
      {
        "title": "Related articles",
        "slug": "related-articles",
        "items": []
      }
    ],
    "readingTime": "3 min read"
  },
  {
    "title": "Creating API keys",
    "summary": "How team admins create, scope, and rotate API keys for programmatic access.",
    "order": 1,
    "published": true,
    "date": new Date("2026-05-27T00:00:00.000Z"),
    "content": "Team owners and admins can create API keys from **Team → Settings → API keys**.\n\n## Before you create a key\n\n1. Decide which endpoints your integration needs — each route requires specific [API key scopes](/docs/knowledge-base/api-key-scopes).\n2. Copy your **team ID** from the same settings page. Public API routes are scoped to that team.\n3. Prefer the minimum scopes required. You can always create a new key with broader access later.\n\n## Create the key\n\n1. Open **Team → Settings → API keys**.\n2. Choose **Create key**.\n3. Name the key for the integration (for example `CI deploy` or `Billing sync`).\n4. Select the scopes your app needs.\n5. Copy the secret immediately — it is shown only once.\n\nKeys use the `{{API_KEY_PREFIX}}_live_` prefix in production. Send them as `Authorization: Bearer …` or `X-API-Key: …` on every request.\n\n## Rotate or revoke\n\nIf a secret is exposed, revoke the key from Team settings and issue a replacement. Update your integration before deleting the old key to avoid downtime.\n\nFor scope details and team role permissions, see the [Knowledge base](/docs/knowledge-base).",
    "_meta": {
      "filePath": "creating-api-keys.mdx",
      "fileName": "creating-api-keys.mdx",
      "directory": ".",
      "extension": "mdx",
      "path": "creating-api-keys"
    },
    "mdxModule": "helpCenterPosts/creating-api-keys",
    "tableOfContents": [
      {
        "title": "Before you create a key",
        "slug": "before-you-create-a-key",
        "items": []
      },
      {
        "title": "Create the key",
        "slug": "create-the-key",
        "items": []
      },
      {
        "title": "Rotate or revoke",
        "slug": "rotate-or-revoke",
        "items": []
      }
    ],
    "readingTime": "1 min read"
  },
  {
    "title": "Introduction",
    "summary": "Learn how to use the product, manage your team, and integrate with our API.",
    "order": 0,
    "published": true,
    "date": new Date("2026-05-27T00:00:00.000Z"),
    "content": "Welcome to the help center. These guides explain how the product works for admins and integrators — without assuming you have already read the API reference.\n\n## What you'll find here\n\n- **Documentation** — onboarding, team setup, [audit log](/docs/audit-log), [creating API keys](/docs/creating-api-keys), and other workflows\n- **API reference** — every `/api/v1` endpoint with examples\n- **Knowledge base** — short lookups for scopes, roles, [audit log events](/docs/knowledge-base/audit-log-events), and common errors\n\n## Need an integration?\n\nIf you are building against the REST API, start with [SDK clients](/docs/api-reference/guides/getting-started) in the API reference, then create a team-scoped key under **Team → Settings → API keys**.",
    "_meta": {
      "filePath": "introduction.mdx",
      "fileName": "introduction.mdx",
      "directory": ".",
      "extension": "mdx",
      "path": "introduction"
    },
    "mdxModule": "helpCenterPosts/introduction",
    "tableOfContents": [
      {
        "title": "What you'll find here",
        "slug": "what-youll-find-here",
        "items": []
      },
      {
        "title": "Need an integration?",
        "slug": "need-an-integration",
        "items": []
      }
    ],
    "readingTime": "1 min read"
  }
]